// Durable named Story snapshots stored alongside the project paper edit.
// Clips are small immutable descriptions; media and interview transcripts are
// referenced from paper.sources and must never be duplicated in each version.
export const MAX_STORY_VERSIONS=20;
const SNAPSHOT_KEYS=['blocks','sectionHeadings','sourceColourOrder','targetRuntimeSeconds',
  'sequenceRate','aiStoryTitle','aiStorySummary','refine'];
const clone=value=>JSON.parse(JSON.stringify(value));
const own=(obj,key)=>Object.prototype.hasOwnProperty.call(obj,key);
export function storySnapshot(paper){
  const snapshot={};
  for(const key of SNAPSHOT_KEYS)if(own(paper||{},key)&&paper[key]!==undefined)snapshot[key]=clone(paper[key]);
  if(!Array.isArray(snapshot.blocks))snapshot.blocks=[];
  if(!Array.isArray(snapshot.sectionHeadings))snapshot.sectionHeadings=[];
  return snapshot;
}
export const snapshotSignature=snapshot=>JSON.stringify(storySnapshot(snapshot));
const caption=value=>String(value||'').trim().slice(0,90);
const assertVersion=(paper,id)=>{
  const version=(paper.storyVersions||[]).find(item=>item.id===id);
  if(!version)throw new Error('This Story version no longer exists.');
  if(!version.snapshot||!Array.isArray(version.snapshot.blocks))throw new Error('This version has invalid saved Story data.');
  return version;
};
export function saveStoryVersion(paper,{title,note='',now=new Date().toISOString(),id=()=>crypto.randomUUID()}={}){
  const name=caption(title);
  if(!name)throw new Error('Give the version a name.');
  if(String(note).length>300)throw new Error('Version notes cannot exceed 300 characters.');
  if(!Array.isArray(paper?.blocks)||!paper.blocks.length)throw new Error('Add a Story passage before saving a version.');
  const versions=paper.storyVersions||[];
  if(versions.length>=MAX_STORY_VERSIONS)throw new Error('You have 20 saved versions. Delete an unused version to make room.');
  const version={id:id(),title:name,note:String(note).trim(),createdAt:now,snapshot:storySnapshot(paper)};
  return {...paper,storyVersions:[...versions,version]};
}
export function deleteStoryVersion(paper,id){
  assertVersion(paper,id);
  return {...paper,storyVersions:(paper.storyVersions||[]).filter(v=>v.id!==id)};
}
export function restoreStoryVersion(paper,id,{now=new Date().toISOString(),newId=()=>crypto.randomUUID()}={}){
  const target=assertVersion(paper,id);
  const existing=paper.storyVersions||[];
  if(existing.length>=MAX_STORY_VERSIONS)
    throw new Error('To restore safely, first delete an unused saved version. Restoring creates a backup of your current Story.');
  for(const block of target.snapshot.blocks){
    const source=paper.sources?.[block.sourceId];
    if(!source)throw new Error('Cannot restore: a referenced interview is no longer available in this project.');
    if(!Number.isFinite(block.start)||!Number.isFinite(block.end)||block.start<0||block.end<=block.start
      ||!Number.isFinite(source.duration)||block.end>source.duration+.02)
      throw new Error('Cannot restore: this version contains a passage outside its original interview.');
  }
  const backup={id:newId(),title:'Before restore · '+target.title,note:'Automatic safety copy made when restoring '+target.title,
    createdAt:now,snapshot:storySnapshot(paper)};
  const restored=storySnapshot(target.snapshot);
  const preserved={...paper};
  for(const key of SNAPSHOT_KEYS)delete preserved[key];
  return {...preserved,...restored,storyVersions:[...existing,backup]};
}
// Automatic AI recovery is separate from the 20 user-named milestone versions.
// Keep a small rolling history, including the EMPTY Assembly before the first AI edit.
export const MAX_AI_CHECKPOINTS=12;
export function checkpointBeforeAiChange(paper,next,action,{now=new Date().toISOString(),id=()=>crypto.randomUUID()}={}){
 if(!paper||!next||snapshotSignature(paper)===snapshotSignature(next))return next;
 const title='Before '+String(action||'AI edit').trim().slice(0,65);
 const entry={id:id(),title,createdAt:now,snapshot:storySnapshot(paper)};
 const history=[...(paper.aiCheckpoints||[]),entry].slice(-MAX_AI_CHECKPOINTS);
 return {...next,aiCheckpoints:history};
}
export function restoreAiCheckpoint(paper,id,{now=new Date().toISOString(),newId=()=>crypto.randomUUID()}={}){
 const checkpoint=(paper.aiCheckpoints||[]).find(entry=>entry.id===id);
 if(!checkpoint||!checkpoint.snapshot||!Array.isArray(checkpoint.snapshot.blocks))
  throw new Error('This automatic recovery point is not available.');
 for(const block of checkpoint.snapshot.blocks){
  const source=paper.sources?.[block.sourceId];
  if(!source)throw new Error('Cannot restore: a referenced interview is no longer available in this project.');
  if(!Number.isFinite(block.start)||!Number.isFinite(block.end)||block.start<0||block.end<=block.start
    ||!Number.isFinite(source.duration)||block.end>source.duration+.02)
   throw new Error('Cannot restore: a recovered passage falls outside its source interview.');
 }
 if(snapshotSignature(paper)===snapshotSignature(checkpoint.snapshot))return paper;
 const backup={id:newId(),title:'Before recovery restore',createdAt:now,snapshot:storySnapshot(paper)};
 const history=[...(paper.aiCheckpoints||[]),backup].slice(-MAX_AI_CHECKPOINTS);
 const preserved={...paper};
 for(const key of SNAPSHOT_KEYS)delete preserved[key];
 return {...preserved,...storySnapshot(checkpoint.snapshot),aiCheckpoints:history};
}

// Longest increasing subsequence over common IDs marks true reorders while
// ignoring index shifts caused solely by insertions or deletions.
function stableCommon(rightIds,leftPosition){
  const pairs=rightIds.filter(id=>leftPosition.has(id)).map(id=>({id,n:leftPosition.get(id)}));
  if(!pairs.length)return new Set();
  const tails=[],previous=Array(pairs.length).fill(-1);
  for(let i=0;i<pairs.length;i++){
    let lo=0,hi=tails.length;
    while(lo<hi){const mid=(lo+hi)>>1;if(pairs[tails[mid]].n<pairs[i].n)lo=mid+1;else hi=mid;}
    if(lo>0)previous[i]=tails[lo-1];
    tails[lo]=i;
  }
  let cursor=tails.at(-1);
  const stable=new Set();
  while(cursor!==-1&&cursor!==undefined){stable.add(pairs[cursor].id);cursor=previous[cursor];}
  return stable;
}
const edits=(a,b)=>a.sourceId!==b.sourceId||a.start!==b.start||a.end!==b.end||
  a.text!==b.text||a.priority!==b.priority||a.editorialSection!==b.editorialSection||
  a.editorialReason!==b.editorialReason||a.editorialContext!==b.editorialContext||
  a.editorialFlowReason!==b.editorialFlowReason;
const footage=blocks=>blocks.reduce((n,b)=>n+Math.max(0,(b.end||0)-(b.start||0)),0);
const compact=block=>({id:block.id,sourceId:block.sourceId,text:block.text||'',
  start:block.start,end:block.end,priority:block.priority||''});
export function compareStoryVersions(before,after){
  const left=storySnapshot(before),right=storySnapshot(after);
  const older=new Map(left.blocks.map((b,i)=>[b.id,{...b,index:i}]));
  const newer=new Map(right.blocks.map((b,i)=>[b.id,{...b,index:i}]));
  const positions=new Map(left.blocks.map((b,i)=>[b.id,i]));
  const stable=stableCommon(right.blocks.map(b=>b.id),positions);
  const added=[],removed=[],moved=[],changed=[],unchanged=[];
  const timeline=right.blocks.map((block,index)=>{
    const original=older.get(block.id);
    const entry={...compact(block),position:index+1,previousPosition:original?original.index+1:null,
      added:!original,moved:!!original&&!stable.has(block.id),changed:!!original&&edits(original,block)};
    if(entry.added)added.push(entry);
    else {
      if(entry.moved)moved.push(entry);
      if(entry.changed)changed.push(entry);
      if(!entry.moved&&!entry.changed)unchanged.push(entry);
    }
    return entry;
  });
  for(let i=0;i<left.blocks.length;i++){
    const b=left.blocks[i];
    if(!newer.has(b.id))removed.push({...compact(b),position:i+1});
  }
  const former=new Map(left.sectionHeadings.map(h=>[h.id,h]));
  const latter=new Map(right.sectionHeadings.map(h=>[h.id,h]));
  const headingAdded=right.sectionHeadings.filter(h=>!former.has(h.id));
  const headingRemoved=left.sectionHeadings.filter(h=>!latter.has(h.id));
  const headingChanged=right.sectionHeadings.filter(h=>{
    const p=former.get(h.id);
    return p&&(p.title!==h.title||p.beforeIndex!==h.beforeIndex);
  }).map(h=>({...h,previous:former.get(h.id)}));
  const targetChanged=left.targetRuntimeSeconds!==right.targetRuntimeSeconds;
  const editorialChanged=left.aiStoryTitle!==right.aiStoryTitle||left.aiStorySummary!==right.aiStorySummary||JSON.stringify(left.sequenceRate)!==JSON.stringify(right.sequenceRate);
  const hasChanges=added.length+removed.length+moved.length+changed.length+
    headingAdded.length+headingRemoved.length+headingChanged.length+(targetChanged?1:0)+(editorialChanged?1:0)>0;
  return {added,removed,moved,changed,unchanged,timeline,
    headings:{added:headingAdded,removed:headingRemoved,changed:headingChanged},
    before:{passages:left.blocks.length,seconds:footage(left.blocks)},
    after:{passages:right.blocks.length,seconds:footage(right.blocks)},
    targetChanged,editorialChanged,hasChanges};
}
